/* eslint-disable react-hooks/exhaustive-deps */
import {
  Flex,
  Box,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AlertComponent from "components/Alert";
import {
  useCreateProcessCommentMutation,
  useGetApprovalTaskQuery,
  useGetProcessCommentsQuery,
} from "redux/services/approvalProcessor";
import LoadingOverlay from "components/LoadingOverlay";
import { DatasetDownloadResponse } from "redux/services/dataLoader/types";
import {
  getOrganisationMembership,
  renderContentTypeAsExtensionFile,
} from "utils";
import { useGetDocumentMutation } from "redux/services/documentRepository";
import {
  ApprovalTaskStatues,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  Roles,
} from "common";
import { useGetProfileQuery } from "redux/services/membership";
import {
  ProcessComment,
  ProcessCommentsRequest,
} from "redux/services/approvalProcessor/types";
import ProcessDeclineDialog from "pages/ApprovalDetail/ProcessDeclineDialog";
import { useApprovalDetailsService } from "pages/ApprovalDetail/ApprovalDetails.service";
import ApprovalDetailsContent from "pages/ApprovalDetail/ApprovalDetailsContent";

const ApprovalDetails = () => {
  const { paymentId } = useParams() as any;
  const toast = useToast();

  const { getApprovalStatus } = useApprovalDetailsService();

  const { data: ProfileData } = useGetProfileQuery();
  const membershipOrganisation = getOrganisationMembership(
    ProfileData?.data?.memberships
  );
  const orgToken = membershipOrganisation?.token;

  const isPaymentOpsRole = ProfileData?.data?.listRoles?.includes(
    Roles.PaymentOps
  );
  const isBusinessApproverRole = ProfileData?.data?.listRoles?.includes(
    Roles.BusinessApprover
  );
  const isBUApproverRole = ProfileData?.data?.listRoles?.includes(Roles.BUApprover);

  const [selectedProofDocument, setSelectedProofDocument] = useState(undefined);
  const [selectedPayrollFile, setSelectedPayrollFile] = useState(undefined);
  const [firstButtonText, setFirstButtonText] = useState("");
  const [secondButtonText, setSecondButtonText] = useState("");

  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    type?: ApprovalTaskStatues;
  }>({ isOpen: false });

  const [getProcessCommentsParamsRequest, setGetProcessCommentsParamsRequest] =
    useState<ProcessCommentsRequest>({
      orgToken,
      pageNumber: DEFAULT_PAGE,
      pageSize: DEFAULT_PAGE_SIZE / 2,
      processId: "",
    });

  const [commentValue, setCommentValue] = useState<string>();
  const [commentsData, setCommentsData] = useState<ProcessComment[]>([]);

  const {
    data: processComments,
    isFetching: isFetchingProcessComments,
    isLoading: isLoadingProcessComments,
  } = useGetProcessCommentsQuery(getProcessCommentsParamsRequest, {
    skip: !getProcessCommentsParamsRequest.processId,
    refetchOnMountOrArgChange: true,
  });

  const isLoadMoreComments =
    getProcessCommentsParamsRequest.pageNumber <
    Math.ceil(
      (processComments?.paging?.totalRecords || 0) /
      getProcessCommentsParamsRequest.pageSize
    );

  const [
    createComment,
    { isLoading: createCommentLoading, data: createCommentData },
  ] = useCreateProcessCommentMutation();

  const {
    data: approvalTasks,
    isLoading,
    isError,
    isSuccess,
    refetch: getApprovalTasks,
  } = useGetApprovalTaskQuery(
    {
      referenceNumber: paymentId,
      orgToken,
    },
    { refetchOnMountOrArgChange: true }
  );

  const approvalTask = approvalTasks?.data[0];

  const isEnableForChangeStatus =
    (isPaymentOpsRole &&
      getApprovalStatus()?.value === ApprovalTaskStatues.PROCESSING) ||
    (isBusinessApproverRole &&
      getApprovalStatus()?.value === ApprovalTaskStatues.PENDING) ||
    (isBUApproverRole &&
      getApprovalStatus()?.label === "Reviewed");

  const approverProcessed = approvalTask?.approvalTasks?.find(
    (appover) =>
      appover?.status === ApprovalTaskStatues.APPROVED &&
      appover.levelId === Roles.PaymentOps
  );

  const approverApproved = approvalTask?.approvalTasks?.find(
    (appover) =>
      appover?.status === ApprovalTaskStatues.APPROVED &&
      appover.levelId === Roles.BusinessApprover
  );

  const approverRejected = approvalTask?.approvalTasks?.find(
    (appover) => appover?.status === ApprovalTaskStatues.REJECTED
  );

  const buApprover = approvalTask?.approvalTasks?.find(
    (appover) =>
      appover?.status === ApprovalTaskStatues.APPROVED &&
      appover.levelId === Roles.BUApprover
  );

  const [
    downloadFile,
    { isSuccess: isDownloadDataFetchSuccess, data: downloadedFile },
  ] = useGetDocumentMutation();

  const [
    downloadProofFile,
    { isSuccess: isDownloadProofDocumentSuccess, data: proofFile },
  ] = useGetDocumentMutation();

  useEffect(() => {
    if (downloadedFile) {
      saveDownload(downloadedFile);
    }
  }, [downloadedFile, isDownloadDataFetchSuccess]);

  useEffect(() => {
    if (proofFile) {
      if (renderContentTypeAsExtensionFile(proofFile?.data?.name)) {
        const bs64file = proofFile?.data?.content;
        const linkSource = `data:${renderContentTypeAsExtensionFile(
          proofFile?.data?.name
        )};base64,${bs64file}`;
        const downloadLink = document.createElement("a");
        downloadLink.href = linkSource;
        downloadLink.download = proofFile?.data?.name as string;
        downloadLink.click();
        downloadLink.remove();
      } else {
        toast({
          title: "The file extension is not supported",
          description: "",
          status: "error",
          duration: 9000,
          position: "top-right",
          isClosable: true,
        });
      }

      setSelectedProofDocument(undefined);
    }
  }, [isDownloadProofDocumentSuccess, proofFile]);

  const saveDownload = (datatoDownload: DatasetDownloadResponse) => {
    if (approvalTasks?.data && approvalTasks?.data?.length > 0) {
      const approvalTask = approvalTasks?.data[0];
      const overallInformation = JSON.parse(
        approvalTask?.processVariables?.overallInformation
      );
      if (datatoDownload?.data?.content) {
        if (renderContentTypeAsExtensionFile(datatoDownload?.data?.name)) {
          const bs64file = datatoDownload?.data?.content;
          const linkSource = `data:application/csv;base64,${bs64file}`;
          const downloadLink = document.createElement("a");
          downloadLink.href = linkSource;
          downloadLink.download = overallInformation?.payrollDocumentName;
          downloadLink.click();
          downloadLink.remove();
        } else {
          toast({
            title: "The file extension is not supported",
            description: "",
            status: "error",
            duration: 9000,
            position: "top-right",
            isClosable: true,
          });
        }
      }
      setSelectedPayrollFile(undefined);
    }
  };

  useEffect(() => {
    if (selectedPayrollFile) {
      downloadFile({ documentId: selectedPayrollFile, orgToken });
    }
  }, [selectedPayrollFile]);

  useEffect(() => {
    if (selectedProofDocument) {
      downloadProofFile({ documentId: selectedProofDocument, orgToken });
    }
  }, [selectedProofDocument]);

  useEffect(() => {
    if (approvalTask)
      setGetProcessCommentsParamsRequest({
        ...getProcessCommentsParamsRequest,
        pageNumber: DEFAULT_PAGE,
        processId: approvalTask?.id,
      });
  }, [approvalTask]);

  useEffect(() => {
    if (createCommentData?.data) {
      setCommentValue("");
      setCommentsData([createCommentData?.data, ...commentsData]);
    }
  }, [createCommentData]);

  useEffect(() => {
    const dataFetching = processComments?.data || [];
    const isFirstFetching =
      getProcessCommentsParamsRequest.pageNumber === DEFAULT_PAGE;
    setCommentsData(
      isFirstFetching ? dataFetching : [...commentsData, ...dataFetching]
    );
  }, [processComments]);

  useEffect(() => {
    if (isBusinessApproverRole && getApprovalStatus()?.label === "Submitted") {
      setFirstButtonText("Proceed");
      setSecondButtonText("Reject");
      return;
    }

    if (isPaymentOpsRole && (getApprovalStatus()?.label === "Reviewed" || getApprovalStatus()?.label === "Approved")) {
      setFirstButtonText("Process");
      setSecondButtonText("Decline");
      return;
    }

    if (isBUApproverRole && getApprovalStatus()?.label === "Reviewed") {
      setFirstButtonText("Approve");
      setSecondButtonText("Disapprove");
    }
  }, [isBusinessApproverRole, getApprovalStatus, isPaymentOpsRole]);

  const renderContent = () => {
    if (approvalTasks?.data && approvalTasks?.data?.length > 0) {
      return (
        <ApprovalDetailsContent
          isPaymentOpsRole={isPaymentOpsRole}
          isBusinessApproverRole={isBusinessApproverRole}
          isBUApproverRole={isBUApproverRole}
          getApprovalStatus={getApprovalStatus}
          approverProcessed={approverProcessed}
          approverRejected={approverRejected}
          approverApproved={approverApproved}
          buApprover={buApprover}
          isEnableForChangeStatus={isEnableForChangeStatus}
          firstButtonText={firstButtonText}
          secondButtonText={secondButtonText}
          setDialogState={setDialogState}
          isLoadingProcessComments={isLoadingProcessComments}
          commentsData={commentsData}
          isFetchingProcessComments={isFetchingProcessComments}
          isLoadMoreComments={isLoadMoreComments}
          getProcessCommentsParamsRequest={getProcessCommentsParamsRequest}
          setGetProcessCommentsParamsRequest={setGetProcessCommentsParamsRequest}
          createCommentLoading={createCommentLoading}
          commentValue={commentValue}
          createComment={createComment}
          orgToken={orgToken}
          approvalTask={approvalTask}
          setSelectedProofDocument={setSelectedProofDocument}
          setSelectedPayrollFile={setSelectedPayrollFile}
          ProfileData={ProfileData}
          setCommentValue={setCommentValue}
        />
      )
    }
  };

  return (
    <Flex
      w="100%"
      h="100%"
      p="20px"
      flexDir={"column"}
      className="ApprovalDetail"
    >
      <Text textStyle={"h1"}>
        {isPaymentOpsRole && "Payment Acknowledgement"}
        {(isBusinessApproverRole || isBUApproverRole) && "Payment Approval"}
      </Text>
      <Box w={"100%"} mt={"50px"}>
        {(!approvalTasks?.data ||
          approvalTasks?.data?.length === 0 ||
          isError) &&
          isSuccess && (
            <AlertComponent
              status="warning"
              title="Cannot found this payroll request"
            />
          )}

        {isLoading ? (
          <LoadingOverlay data-testid="loading-screen" h="100%" />
        ) : (
          renderContent()
        )}
      </Box>

      <ProcessDeclineDialog
        {...dialogState}
        approvalTask={approvalTask}
        onClose={() => setDialogState({ isOpen: false })}
        onSuccess={() => {
          getApprovalTasks();
        }}
      />
    </Flex>
  );
};

export default ApprovalDetails;
